import cn from 'classnames'
import {useEffect, useMemo, useRef, useState} from 'react'

import {BTN_PRIMARY, BTN_SECONDARY, GAME_RULES_URL, GAME_TERMS_URL} from '../constants'
import type {Answer, Persona, Question, SubmitEmailData} from '../types'
import type {FraudGameShareType} from '../useFraudGameAnalytics'
import type {LeaderboardData} from '../useFraudGameApi'
import {getPersona} from '../utils'
import {CountdownTimer} from './CountdownTimer'
import {JoinLeaderboardModal} from './JoinLeaderboardModal'
import {ScoreCardImage} from './ScoreCardImage'
import {ShareModal} from './ShareModal'

export const ResultsAlt = ({
  score,
  answers,
  questions,
  playCount,
  sessionStart,
  hasSubmittedEmail,
  username,
  playerId,
  leaderboardData,
  isSubmittingEmail,
  emailError,
  onPlayAgain,
  onSubmitEmail,
  onDetailsFormOpen,
  onShareModalOpen,
  onShareClick,
  competitionEndDate,
}: {
  score: number
  answers: Answer[]
  questions: Question[]
  playCount: number
  sessionStart: number
  hasSubmittedEmail: boolean
  username: string
  playerId: string
  leaderboardData?: LeaderboardData | null
  isSubmittingEmail?: boolean
  emailError?: string | null
  onPlayAgain: () => void
  onSubmitEmail: (data: SubmitEmailData) => Promise<void>
  onDetailsFormOpen: () => void
  onShareModalOpen: () => void
  onShareClick: (shareType: FraudGameShareType, shareUrl: string) => void
  competitionEndDate?: string | null
}) => {
  const persona: Persona = getPersona(score)
  const cardRef = useRef<HTMLDivElement>(null)
  const badgeColRef = useRef<HTMLDivElement>(null)
  const [badgeColHeight, setBadgeColHeight] = useState<number | undefined>(undefined)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false)
  const [evidenceOpen, setEvidenceOpen] = useState(false)
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null)
  const solvedCount = answers.filter((a) => a.correct).length

  // Measure badge column height so the leaderboard can match it
  useEffect(() => {
    if (badgeColRef.current) {
      setBadgeColHeight(badgeColRef.current.offsetHeight)
    }
  }, [hasSubmittedEmail])

  const elapsedSeconds = useMemo(() => {
    if (!sessionStart) {
      return 0
    }

    return Math.floor((Date.now() - sessionStart) / 1000)
  }, [sessionStart])

  const caseNumber = useMemo(() => {
    const year = new Date().getFullYear()
    const id = String(Math.floor(1000 + Math.random() * 9000))
    return `GM-${year}-${id}`
  }, [])

  const allPlayers = leaderboardData?.topTenPlayers ?? []
  const currentPlayer = leaderboardData?.currentPlayer
  const totalPlayers = leaderboardData?.totalPlayers ?? 0
  const userRank = currentPlayer?.position ?? 0

  const handleShare = () => {
    onShareModalOpen()
    setIsShareModalOpen(true)
  }

  const handleOpenJoinModal = () => {
    onDetailsFormOpen()
    setIsJoinModalOpen(true)
  }

  const handleSubmitEmail = async (data: SubmitEmailData) => {
    await onSubmitEmail(data)
    setIsJoinModalOpen(false)
  }

  return (
    <div className="min-h-screen px-6 pb-10 pt-16">
      {/* Hidden container for image export */}
      <div aria-hidden style={{height: 0, overflow: 'hidden', pointerEvents: 'none'}}>
        <ScoreCardImage
          ref={cardRef}
          score={score}
          persona={persona}
          solvedCount={solvedCount}
          totalQuestions={questions.length}
          elapsedSeconds={elapsedSeconds}
          rankPercent={totalPlayers > 0 ? Math.round((userRank / totalPlayers) * 100) : 0}
          caseNumber={caseNumber}
        />
      </div>

      {/* ── Header ── */}
      <div className="relative mx-auto mb-6 w-fit" style={{animation: 'fade-up 800ms ease-out both'}}>
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-col items-center gap-2">
            <img
              src="/icons/moss-logo-v2.svg"
              alt="Moss"
              className="h-[12px] w-[50px] opacity-50 brightness-0 invert"
            />
            <span className="text-16 uppercase leading-none tracking-[0.8px] text-grey-300">Papertrails</span>
          </div>
          <h1 className="text-center text-28 font-medium leading-8 text-grey-110">The Verdict</h1>
        </div>
      </div>

      {/* ── Layout: Player badge + Leaderboard ── */}
      <div className="mx-auto max-w-[960px]">
        <div className="flex flex-col items-center gap-5 md:flex-row md:items-start md:justify-center">
          {/* Player badge column */}
          <div ref={badgeColRef} className="flex w-full flex-col gap-4 md:w-[300px] md:shrink-0">
            <div
              className="flex flex-col items-center rounded-xl border border-grey-900 bg-[#171717] px-6 py-5"
              style={{animation: 'fade-up 800ms ease-out 400ms both'}}
            >
              {/* Score card image */}
              <div className="mb-4" style={{perspective: '600px'}}>
                <div
                  className="group relative h-[250px] w-[195px] cursor-pointer overflow-hidden rounded-lg"
                  style={{
                    transformStyle: 'preserve-3d',
                    transform: 'rotateX(3deg) rotateY(-2deg)',
                    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                    boxShadow: '1px 2px 8px rgba(0,0,0,0.3)',
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect()
                    const x = (e.clientX - rect.left) / rect.width - 0.5
                    const y = (e.clientY - rect.top) / rect.height - 0.5
                    e.currentTarget.style.transform = `rotateX(${-y * 12}deg) rotateY(${x * 12}deg)`
                    e.currentTarget.style.boxShadow = `${-x * 6}px ${-y * 6 + 3}px 14px rgba(0,0,0,0.4)`
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'rotateX(3deg) rotateY(-2deg)'
                    e.currentTarget.style.boxShadow = '1px 2px 8px rgba(0,0,0,0.3)'
                  }}
                >
                  <img src={persona.cardImage} alt={persona.title} className="size-full object-cover" />
                  <div
                    className="pointer-events-none absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
                    style={{
                      background:
                        'linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 55%, transparent 70%)',
                      backgroundSize: '250% 100%',
                      animation: 'shimmer 3500ms ease-in-out infinite',
                    }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="mt-2 flex w-full flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="font-typewriter text-14 text-grey-300">Score</span>
                  <span className="text-20 tabular-nums leading-6 text-green-neon">
                    {score}
                    <span className="text-14 text-grey-450"> / 100</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-typewriter text-14 text-grey-300">Solved</span>
                  <span className="text-20 tabular-nums leading-6 text-green-neon">
                    {solvedCount}
                    <span className="text-14 text-grey-450"> / {questions.length}</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-typewriter text-14 text-grey-300">Time</span>
                  <span className="text-20 tabular-nums leading-6 text-green-neon">
                    {Math.floor(elapsedSeconds / 60)}
                    <span className="text-14 text-grey-450"> m </span>
                    {elapsedSeconds % 60}
                    <span className="text-14 text-grey-450"> s</span>
                  </span>
                </div>
              </div>

              {hasSubmittedEmail && (
                <div className="mt-4 flex w-full items-center gap-2" style={{animation: 'fade-in 600ms ease-out'}}>
                  {playCount <= 1 && (
                    <button onClick={onPlayAgain} className={cn(BTN_SECONDARY, 'flex-1')}>
                      Replay
                    </button>
                  )}
                  <button onClick={handleShare} className={cn(BTN_PRIMARY, 'flex-1')}>
                    Share
                  </button>
                </div>
              )}
            </div>

            {/* ── Evidence review ── */}
            <div
              className="rounded-xl border border-grey-900 bg-[#171717] px-5 py-4"
              style={{animation: 'fade-up 800ms ease-out 800ms both'}}
            >
              <button
                onClick={() => setEvidenceOpen((o) => !o)}
                className="flex w-full items-center justify-between text-left"
              >
                <p className="font-typewriter text-16 font-medium text-grey-110">Evidence review</p>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  className={cn('text-grey-450 transition-transform duration-200', {
                    'rotate-180': evidenceOpen,
                  })}
                >
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {evidenceOpen && (
                <div className="mt-3 flex flex-col gap-1.5">
                  {questions.map((q) => {
                    const answer = answers.find((a) => a.questionId === q.id)
                    const solved = answer?.correct ?? false
                    const isExpanded = expandedQuestion === q.id

                    return (
                      <div key={q.id} className="overflow-hidden rounded-lg border border-grey-900">
                        <button
                          onClick={() => setExpandedQuestion(isExpanded ? null : q.id)}
                          className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-[#1e1e1e]"
                        >
                          {solved ? (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 16 16"
                              fill="none"
                              className="shrink-0 text-[#458223]"
                            >
                              <path
                                d="M3.5 8.5L6.5 11.5L12.5 4.5"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 16 16"
                              fill="none"
                              className="shrink-0 text-[#B8211C]"
                            >
                              <path
                                d="M4 4L12 12M12 4L4 12"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          )}
                          <span className="flex-1 truncate text-12 font-medium text-grey-200">{q.shortLabel}</span>
                          <span
                            className={cn(
                              'shrink-0 text-[9px] uppercase opacity-50',
                              solved ? 'text-[#458223]' : 'text-[#B8211C]',
                            )}
                          >
                            {solved ? 'Solved' : 'Missed'}
                          </span>
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 16 16"
                            fill="none"
                            className={cn('shrink-0 text-grey-450 transition-transform duration-200', {
                              'rotate-180': isExpanded,
                            })}
                          >
                            <path
                              d="M4 6L8 10L12 6"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        {isExpanded && (
                          <div className="border-t border-grey-900 bg-[#111] p-3">
                            <div className="mb-1 flex items-center gap-1.5">
                              <img src="/icons/moss-logo.svg" alt="" className="size-3.5 brightness-0 invert" />
                              <span className="text-10 font-semibold uppercase tracking-wide text-moss-white">
                                How Moss helps
                              </span>
                            </div>
                            <p className="line-clamp-3 text-12 leading-[16px] text-grey-300">
                              {q.mossInsight.howMossHelps}
                            </p>
                            {/*
                            <a
                              href="https://www.getmoss.com"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-12 text-moss-white underline opacity-80 transition-opacity hover:opacity-100"
                            >
                              Learn more
                              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" className="shrink-0">
                                <path
                                  d="M6 4H12V10M12 4L4 12"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </a>
*/}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Leaderboard */}
          <div className="w-full md:flex-1">
            <div
              className="relative flex flex-col gap-2.5 overflow-hidden text-clip rounded-xl border border-grey-900 bg-[#171717] px-6 py-5"
              style={{
                animation: 'fade-up 800ms ease-out 600ms both',
                minHeight: badgeColHeight ? `${badgeColHeight}px` : undefined,
              }}
            >
              <div className="relative flex min-h-0 flex-1 flex-col">
                {hasSubmittedEmail && (
                  <div style={{animation: 'fade-in 600ms ease-out'}}>
                    {/* Prize cards */}
                    <div className="mb-4 flex items-center justify-between">
                      <p className="font-typewriter text-16 font-medium text-grey-110">Prize buckets</p>
                      <span className="inline-flex shrink-0 items-center gap-1.5 text-12 text-grey-300 opacity-60">
                        <span className="size-1.5 rounded-full" style={{backgroundColor: '#265F4F'}} />
                        {score >= 50 ? (
                          <>
                            <span style={{color: '#2E7D32'}}>You&apos;re in</span> for Finance Merch
                          </>
                        ) : (
                          'Score 50+ for Finance Merch'
                        )}
                      </span>
                    </div>
                    <div className="mb-6 grid grid-cols-1 gap-3 max-md:grid-cols-2 lg:grid-cols-2">
                      {/* Active prize — voucher */}
                      <div className="flex flex-1 items-center gap-3 rounded-xl border border-grey-900 bg-[#171717] px-4 py-3">
                        <img
                          src="/images/fraud-game/prize-treatwell.png"
                          alt="Treatwell"
                          className="size-12 shrink-0 rounded-md object-cover opacity-75"
                        />
                        <div className="min-w-0 flex-1 opacity-40">
                          <p className="whitespace-nowrap text-14 font-medium text-grey-110">
                            &pound;150 Treatwell voucher
                          </p>
                          <p className="text-12 text-grey-300">Highest scorer takes it.</p>
                        </div>
                        <span className="flex shrink-0 items-center gap-1 px-2 py-1 text-14 font-medium text-[#2E7D32]">
                          <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                            <path
                              d="M3 7L6 10L11 4"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          You&apos;re in
                        </span>
                      </div>

                      {/* Locked prize — AirPods */}
                      <div
                        className="relative flex flex-1 items-center gap-3 overflow-hidden rounded-[11px] border-[1.4px] border-[#3d3c3c] px-4 py-3"
                        style={{
                          background: 'linear-gradient(144deg, rgba(16,16,16,0.5) 2.35%, rgba(52,52,52,0.5) 96.08%)',
                          backdropFilter: 'blur(2.7px)',
                        }}
                      >
                        <div
                          className="absolute inset-0"
                          style={{
                            background:
                              'linear-gradient(110deg, transparent 0%, transparent 40%, rgba(255,255,255,0.04) 48%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.04) 52%, transparent 60%, transparent 100%)',
                            backgroundSize: '250% 100%',
                            animation: 'shimmer 5s linear infinite',
                          }}
                        />
                        <img
                          src="/images/fraud-game/prize-airpods.png"
                          alt="AirPods"
                          className="relative size-12 shrink-0 rounded-md object-cover opacity-75"
                        />
                        <div className="relative flex-1">
                          <p className="text-14 font-medium text-grey-110">Apple AirPods</p>
                          <p className="text-12 text-grey-300">For highest scoring sharer.</p>
                        </div>
                        <button
                          onClick={handleShare}
                          className="relative shrink-0 rounded-[4px] border border-moss-white bg-transparent px-3 py-1.5 text-12 font-semibold uppercase text-moss-white transition-all hover:bg-moss-white hover:text-moss-black"
                        >
                          Unlock
                        </button>
                      </div>
                    </div>

                    {/* Leaderboard header */}
                    <div className="mb-4 flex items-center justify-between">
                      <div className="flex items-baseline gap-3">
                        <p className="font-typewriter text-16 font-medium text-grey-110">Leaderboard</p>
                        <span className="whitespace-nowrap text-14 text-grey-450">{totalPlayers} investigators</span>
                      </div>
                      {competitionEndDate && <CountdownTimer endDate={competitionEndDate} />}
                    </div>

                    {(() => {
                      const isInTop3 = userRank > 0 && userRank <= 3
                      const top3 = allPlayers.slice(0, 3)

                      const playersAfterUser =
                        currentPlayer && !isInTop3
                          ? allPlayers.filter((p) => p.position > currentPlayer.position)
                          : allPlayers.slice(3)

                      return (
                        <>
                          {/* Fixed top 3 */}
                          <div className="flex flex-col gap-3">
                            {top3.map((player) => {
                              const isCurrentUser = currentPlayer?.position === player.position

                              return (
                                <div
                                  key={player.position}
                                  className={cn(
                                    'flex h-14 shrink-0 items-center gap-2 rounded-xl border bg-[#171717] p-4',
                                    isCurrentUser ? 'border-moss-white' : 'border-[#717171]',
                                  )}
                                >
                                  <span className="shrink-0 text-14 font-bold leading-4 text-grey-450">
                                    {player.position}
                                  </span>
                                  <span className="flex-1 text-14 leading-4 text-grey-110">{player.username}</span>
                                  {isCurrentUser && (
                                    <span className="shrink-0 rounded bg-[#FCEFB3] px-1.5 py-0.5 text-10 font-medium uppercase text-[#A16A18]">
                                      This is you
                                    </span>
                                  )}
                                  <span className="shrink-0 text-16 leading-4 text-[#E3E2E2]">{player.score} PTS</span>
                                </div>
                              )
                            })}
                          </div>

                          {/* Divider */}
                          <div className="flex flex-col items-center gap-[2px] py-3">
                            <div className="h-[1px] w-full bg-[#3D3C3C]" />
                            <div className="h-[1px] w-full bg-[#3D3C3C]" />
                          </div>

                          {/* Scrollable remaining rows */}
                          <div
                            className="flex max-h-[510px] flex-col gap-3 overflow-y-auto"
                            style={{scrollbarWidth: 'thin', scrollbarColor: '#3D3C3C transparent'}}
                          >
                            {!isInTop3 && currentPlayer && (
                              <div className="flex h-14 shrink-0 items-center gap-2 rounded-xl border border-moss-white bg-[#171717] p-4">
                                <span className="shrink-0 text-14 font-medium leading-4 text-grey-135">
                                  {currentPlayer.position}
                                </span>
                                <span className="text-14 leading-4 text-grey-110">{currentPlayer.username}</span>
                                <span className="shrink-0 rounded bg-[#FCEFB3] px-1.5 py-0.5 text-10 font-medium uppercase text-[#A16A18]">
                                  This is you
                                </span>
                                <span className="flex-1" />
                                <span className="shrink-0 text-16 font-bold leading-4 text-[#E3E2E2]">
                                  {currentPlayer.score} PTS
                                </span>
                              </div>
                            )}
                            {playersAfterUser.map((player) => (
                              <div
                                key={player.position}
                                className="flex h-14 shrink-0 items-center gap-2 rounded-xl border border-grey-900 bg-[#171717] p-4 opacity-50"
                              >
                                <span className="shrink-0 text-14 leading-4 text-grey-450">{player.position}</span>
                                <span className="flex-1 text-14 leading-4 text-grey-110">{player.username}</span>
                                <span className="shrink-0 text-16 leading-4 text-[#E3E2E2]">{player.score} PTS</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )
                    })()}
                  </div>
                )}

                {/* Locked overlay — covers leaderboard only */}
                {!hasSubmittedEmail && (
                  <>
                    {/* Background image */}
                    <div
                      className="pointer-events-none absolute -inset-x-6 -inset-y-5 rounded-xl opacity-70"
                      style={{
                        backgroundImage: 'url(/images/fraud-game/leaderboard-locked-bg.png)',
                        backgroundSize: '70%',
                        backgroundPosition: 'center 48px',
                        backgroundRepeat: 'no-repeat',
                      }}
                    />
                    {/* Top gradient overlay */}
                    <div
                      className="pointer-events-none absolute -inset-x-6 -inset-y-5 rounded-xl backdrop-blur-[2px]"
                      style={{
                        background: 'linear-gradient(180deg, transparent 60%, #171717 85%)',
                      }}
                    />
                    {/* Content */}
                    <div className="relative z-10 flex flex-1 flex-col items-center justify-center">
                      {/* User row */}
                      <div className="mx-auto w-full max-w-[500px]">
                        <div className="flex h-14 items-center gap-2 rounded-xl border border-grey-900 bg-[#171717] p-4">
                          <div className="relative size-7 shrink-0">
                            <img src="/images/fraud-game/star-rank.svg" alt="" className="size-full" />
                            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-[#fef5d5]">
                              ?
                            </span>
                          </div>
                          <span className="text-14 leading-4 text-grey-110">{username}</span>
                          <span className="shrink-0 rounded bg-[#FCEFB3] px-1.5 py-0.5 text-10 font-medium uppercase text-[#A16A18]">
                            This is you
                          </span>
                          <span className="flex-1" />
                          <span className="shrink-0 text-16 font-bold text-[#E3E2E2]">{score} PTS</span>
                        </div>
                      </div>

                      {/* Heading + text */}
                      <h3 className="mt-[40px] text-center text-24 font-medium text-grey-110">Enter the competition</h3>
                      <p className="mx-auto mt-[8px] max-w-[380px] text-center text-13 leading-5 text-grey-300">
                        Add your details to get on the leaderboard. This month&apos;s top scorer wins a{' '}
                        <span className="font-semibold text-grey-200">&pound;150 Treatwell voucher</span>. Once
                        you&apos;re in, share the game to play for{' '}
                        <span className="font-semibold text-grey-200">Apple AirPods</span> instead. Top-scoring sharer
                        wins.
                      </p>

                      {/* Prize cards */}
                      <div className="mx-auto mt-6 grid max-w-[420px] grid-cols-2 gap-3">
                        <div
                          className="flex items-center gap-3 overflow-hidden rounded-[11px] border-[1.4px] border-[#3d3c3c] px-4 py-3"
                          style={{
                            background: 'linear-gradient(144deg, rgba(16,16,16,0.5) 2.35%, rgba(52,52,52,0.5) 96.08%)',
                          }}
                        >
                          <img
                            src="/images/fraud-game/prize-treatwell.png"
                            alt="Treatwell"
                            className="h-[25px] w-[25px] shrink-0 rounded object-cover"
                          />
                          <div className="flex-1">
                            <p className="text-12 font-medium leading-none text-grey-110">
                              &pound;150 Treatwell voucher
                            </p>
                            <p className="mt-0.5 text-10 leading-none text-grey-300">Top scorer wins.</p>
                          </div>
                        </div>
                        <div
                          className="flex items-center gap-3 overflow-hidden rounded-[11px] border-[1.4px] border-[#3d3c3c] px-4 py-3"
                          style={{
                            background: 'linear-gradient(144deg, rgba(16,16,16,0.5) 2.35%, rgba(52,52,52,0.5) 96.08%)',
                          }}
                        >
                          <img
                            src="/images/fraud-game/prize-airpods.png"
                            alt="AirPods"
                            className="h-[25px] w-[25px] shrink-0 rounded object-cover"
                          />
                          <div className="flex-1">
                            <p className="text-12 font-medium leading-none text-grey-110">Apple AirPods</p>
                            <p className="mt-0.5 text-10 leading-none text-grey-300">Top-scoring sharer wins.</p>
                          </div>
                        </div>
                        <div className="col-span-full flex items-center justify-center gap-1.5 px-4 py-1">
                          <span className="size-1.5 shrink-0 rounded-full bg-[#265F4F]" />
                          <p className="text-10 leading-none text-grey-300">
                            Players scoring more than 50 points get a chance to win Finance merch.
                          </p>
                          <span className="size-1.5 shrink-0 rounded-full bg-[#265F4F]" />
                        </div>
                      </div>

                      {/* Unlock button */}
                      <button onClick={handleOpenJoinModal} className={cn(BTN_PRIMARY, 'mx-auto mt-6')}>
                        Join leaderboard
                      </button>
                      <p className="mt-1.5 text-center text-10 leading-4 text-grey-450">
                        We won&apos;t show any of your personal data on the leaderboard.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <JoinLeaderboardModal
        isOpen={isJoinModalOpen}
        onClose={() => setIsJoinModalOpen(false)}
        onSubmit={handleSubmitEmail}
        isSubmitting={isSubmittingEmail}
        serverError={emailError}
      />

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        score={score}
        persona={persona}
        rankPercent={totalPlayers > 0 ? Math.round((userRank / totalPlayers) * 100) : 0}
        cardRef={cardRef}
        playerId={playerId}
        onShareClick={onShareClick}
      />

      <div className="mt-8 pb-4 text-center text-12 text-grey-450">
        <a
          href={GAME_TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline transition-colors hover:text-grey-300"
        >
          Game T&amp;C
        </a>
        {' | '}
        <a
          href={GAME_RULES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline transition-colors hover:text-grey-300"
        >
          Game Rules
        </a>
      </div>
    </div>
  )
}
